import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Tabs, Toggle, cn } from '@chapche/ui';
import {
    POPUP_KEY_DELETE_COOKING_STEP,
} from '../../constant';
import { cookingStepSaveService } from '../../services/cookingStepSaveService';
import { cookingStepDeleteService } from '../../services/cookingStepDeleteService';
import {
    formatStepConditionsSummary,
    validateStep,
} from '../../services/cookingStepConditionUtils';
import DeleteItemIcon from '../../shared/DeleteItemIcon';
import {
    CookingStep,
    CookingStepCondition,
    CookingStepConditionType,
    CookingStepText,
    MeasureBlock,
    RecipeType,
    ReminderKind,
    ReminderPreset,
} from '../../services/recipeTypes';
import { Locale } from '../../services/commonTypes';
import ConditionRow from './ConditionRow';

type CookingStepEditorProps = {
    step: CookingStep;
    recipeType?: string;
};

const REMINDER_KINDS: ReminderKind[] = [
    ReminderKind.PRESET,
    ReminderKind.OFFSET_HOURS,
];

const REMINDER_PRESETS: ReminderPreset[] = [
    ReminderPreset.EVENING_BEFORE,
    ReminderPreset.MORNING_OF,
];

const MEASURE_BLOCKS: MeasureBlock[] = [
    MeasureBlock.NONE,
    MeasureBlock.GRAIN,
    MeasureBlock.LIQUID,
    MeasureBlock.BOTH,
];

const textForLocale = (texts: CookingStepText[] | undefined, locale: string): string => {
    const found = (texts || []).find((item) => item.locale === locale);
    return found ? found.text : '';
};

const upsertText = (
    texts: CookingStepText[] | undefined,
    locale: string,
    value: string,
): CookingStepText[] => {
    const next = [...(texts || [])];
    const index = next.findIndex((item) => item.locale === locale);
    if (index >= 0) {
        next[index] = { ...next[index], text: value };
    } else {
        next.push({ locale, text: value });
    }
    return next;
};

const CookingStepEditor: React.FC<CookingStepEditorProps> = ({ step, recipeType }) => {
    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n);
    const [textLocale, setTextLocale] = useState<string>(Locale.LOCALE_RU);
    const stepErrors = validateStep(step);
    const isKasha = recipeType === RecipeType.RECIPE_TYPE_KASHA
        || recipeType === 'kasha'
        || step.recipeType === 'kasha';

    const reminderKindOptions = REMINDER_KINDS.map((kind) => ({
        value: kind,
        label: t(`app.page.cookingStep.reminderKind.${kind}`),
    }));

    const reminderPresetOptions = REMINDER_PRESETS.map((preset) => ({
        value: preset,
        label: t(`app.page.cookingStep.reminderPreset.${preset}`),
    }));

    const measureBlockOptions = MEASURE_BLOCKS.map((block) => ({
        value: block,
        label: t(`app.page.cookingStep.measureBlock.${block}`),
    }));

    const textLocaleTabs = [
        { id: Locale.LOCALE_RU, label: t('app.page.cookingStep.textLocale.ru') },
        { id: Locale.LOCALE_EN, label: t('app.page.cookingStep.textLocale.en') },
    ];

    const persist = (patch: Partial<CookingStep>) => {
        if (!step.id) {
            return;
        }
        cookingStepSaveService.updateLocalStep(step.id, patch);
    };

    const updateCondition = (index: number, patch: Partial<CookingStepCondition>) => {
        const conditions = (step.conditions || []).map((condition, conditionIndex) => (
            conditionIndex === index ? { ...condition, ...patch } : condition
        ));
        persist({ conditions });
    };

    const removeCondition = (index: number) => {
        persist({
            conditions: (step.conditions || []).filter((_, conditionIndex) => conditionIndex !== index),
        });
    };

    const addCondition = () => {
        persist({
            conditions: [
                ...(step.conditions || []),
                {
                    conditionType: CookingStepConditionType.PRODUCT_FIELD,
                    ingredientGroup: 'base',
                    fieldName: 'before_cooking_rinse_need',
                    operator: 'eq',
                    value: 'true',
                    productCode: '',
                },
            ],
        });
    };

    const setIsReminder = (checked: boolean) => {
        if (!checked) {
            persist({
                isReminder: false,
                reminderKind: null,
                reminderPreset: null,
                reminderOffsetHours: null,
            });
            return;
        }
        persist({
            isReminder: true,
            reminderKind: step.reminderKind || ReminderKind.PRESET,
            reminderPreset: step.reminderPreset || ReminderPreset.EVENING_BEFORE,
            reminderOffsetHours: null,
        });
    };

    const conditions = step.conditions || [];
    const conditionsSummary = formatStepConditionsSummary(
        step,
        t,
        i18n.resolvedLanguage || Locale.LOCALE_RU,
    );

    return (
        <section className="cooking-steps__editor">
            <div className="cooking-steps__editor-toolbar">
                <h2 className="cooking-steps__editor-title">
                    {t('app.page.cookingStep.detailTitle', {
                        code: step.stepCode || '—',
                    })}
                </h2>
                <DeleteItemIcon
                    itemId={step.id}
                    itemDeleteService={cookingStepDeleteService}
                    popupKey={POPUP_KEY_DELETE_COOKING_STEP}
                />
            </div>

            <div className="cooking-steps__section">
                <h3 className="cooking-steps__section-title">
                    {t('app.page.cookingStep.section.instruction')}
                </h3>
                <Tabs
                    items={textLocaleTabs}
                    activeId={textLocale}
                    size="small"
                    onChange={(_index, item) => setTextLocale(item.id)}
                />
                <label className="block__text recipe__form-label cooking-steps__text-label">
                    {t(`app.page.cookingStep.textForUser.${textLocale}`)}
                    <textarea
                        className={cn(
                            'block__content input cooking-steps__textarea box-border w-full rounded-input border border-solid border-light-grey bg-white px-16 py-12 font-montserrat-medium text-l text-ink',
                            textLocale === Locale.LOCALE_RU && stepErrors.textRu
                                ? 'input_error cooking-steps__input_error border-error'
                                : '',
                        )}
                        value={textForLocale(step.texts, textLocale)}
                        onChange={(e) => persist({
                            texts: upsertText(step.texts, textLocale, e.target.value),
                        })}
                    />
                </label>
                {textLocale === Locale.LOCALE_RU && stepErrors.textRu ? (
                    <p className="cooking-steps__field-error">
                        {t('app.page.cookingStep.validation.required')}
                    </p>
                ) : null}
                {stepErrors.prosePlaceholders ? (
                    <p className="cooking-steps__field-error">
                        {t('app.page.cookingStep.validation.noPlaceholders')}
                    </p>
                ) : null}
            </div>

            {isKasha ? (
                <div className="cooking-steps__section">
                    <h3 className="cooking-steps__section-title">
                        {t('app.page.cookingStep.measureBlockLabel')}
                    </h3>
                    <Select
                        fullWidth
                        options={measureBlockOptions}
                        value={step.measureBlock || MeasureBlock.NONE}
                        onChange={(value) => persist({ measureBlock: value as MeasureBlock })}
                    />
                    <p className="cooking-steps__hint">
                        {t('app.page.cookingStep.measureBlockHint')}
                    </p>
                </div>
            ) : null}

            <div className="cooking-steps__section">
                <h3 className="cooking-steps__section-title">
                    {t('app.page.cookingStep.section.meta')}
                </h3>
                <Input
                    fullWidth
                    label={t('app.page.cookingStep.stepCode')}
                    value={step.stepCode || ''}
                    onChange={(e) => persist({ stepCode: e.target.value })}
                />
                {stepErrors.stepCode ? (
                    <p className="cooking-steps__field-error">
                        {t('app.page.cookingStep.validation.required')}
                    </p>
                ) : null}
                <p className="cooking-steps__hint">{t('app.page.cookingStep.stepCodeHint')}</p>
            </div>

            <div className="cooking-steps__section">
                <div className="cooking-steps__section-row">
                    <h3 className="cooking-steps__section-title cooking-steps__section-title_inline">
                        {t('app.page.cookingStep.section.reminder')}
                    </h3>
                    <Toggle
                        checked={Boolean(step.isReminder)}
                        label={t('app.page.cookingStep.isReminder')}
                        onChange={setIsReminder}
                    />
                </div>
                {step.isReminder ? (
                    <div className="cooking-steps__reminder-fields">
                        <label className="block__text recipe__form-label">
                            {t('app.page.cookingStep.reminderKindLabel')}
                            <Select
                                fullWidth
                                options={reminderKindOptions}
                                value={step.reminderKind || ReminderKind.PRESET}
                                onChange={(nextValue) => {
                                    if (nextValue === ReminderKind.PRESET) {
                                        persist({
                                            reminderKind: ReminderKind.PRESET,
                                            reminderPreset: step.reminderPreset || ReminderPreset.EVENING_BEFORE,
                                            reminderOffsetHours: null,
                                        });
                                    } else {
                                        persist({
                                            reminderKind: ReminderKind.OFFSET_HOURS,
                                            reminderPreset: null,
                                            reminderOffsetHours: step.reminderOffsetHours || 12,
                                        });
                                    }
                                }}
                            />
                        </label>
                        {step.reminderKind === ReminderKind.OFFSET_HOURS ? (
                            <Input
                                fullWidth
                                type="number"
                                label={t('app.page.cookingStep.reminderOffsetHours')}
                                min={1}
                                max={72}
                                value={String(step.reminderOffsetHours ?? 12)}
                                onChange={(e) => persist({
                                    reminderOffsetHours: Number(e.target.value) || 1,
                                })}
                            />
                        ) : (
                            <label className="block__text recipe__form-label">
                                {t('app.page.cookingStep.reminderPresetLabel')}
                                <Select
                                    fullWidth
                                    options={reminderPresetOptions}
                                    value={step.reminderPreset || ReminderPreset.EVENING_BEFORE}
                                    onChange={(nextValue) => persist({
                                        reminderPreset: nextValue as ReminderPreset,
                                    })}
                                />
                            </label>
                        )}
                    </div>
                ) : null}
            </div>

            <div className="cooking-steps__section">
                <h3 className="cooking-steps__section-title">
                    {t('app.page.cookingStep.conditions')}
                </h3>
                {conditions.length === 0 ? (
                    <p className="cooking-steps__hint">{t('app.page.cookingStep.conditionsAlways')}</p>
                ) : (
                    <p className="cooking-steps__hint">{t('app.page.cookingStep.conditionsAndHint')}</p>
                )}
                <p className="cooking-steps__step-preview">{conditionsSummary}</p>
                {conditions.map((condition, index) => (
                    <ConditionRow
                        key={`${step.id}-condition-${index}`}
                        condition={condition}
                        onChange={(patch) => updateCondition(index, patch)}
                        onRemove={() => removeCondition(index)}
                    />
                ))}
                <Button variant="outline" onClick={addCondition}>
                    {conditions.length === 0
                        ? t('app.page.cookingStep.addConditionFirst')
                        : t('app.page.cookingStep.addCondition')}
                </Button>
            </div>
        </section>
    );
};

export default CookingStepEditor;
